<script setup lang="ts">
  import { Popover as ArkPopover } from '@ark-ui/vue';
  import type { PopoverInteractOutsideEvent } from '@ark-ui/vue';
  import { TUNDR_ICONS } from '@tundr/theme-assets/tundr-icons/tundr-icons-icons';
  import { computed, provide, reactive, ref, useSlots, watch } from 'vue';
  import { GLOBAL_OVERLAYS_TARGET } from '../config/global-overlays-target';
  import Button from '../Button/Button.vue';
  import { ButtonVariant } from '../Button/Button.types';
  import { DROPDOWN_CONTEXT_KEY } from './Dropdown.context';
  import {
    dropdownContentClass,
    dropdownContentFixedHeightClass,
    dropdownContentPaddingClass,
    dropdownContentResetClass,
    dropdownContentSameWidthClass,
    dropdownContentSelfScrollClass,
    dropdownIconTriggerClass,
    dropdownPositionerClass,
    dropdownTriggerResetClass,
    slotTriggerFullWidthClass,
  } from './Dropdown.css';
  import {
    type DropdownEmits,
    DropdownPlacement,
    type DropdownProps,
  } from './Dropdown.types';

  const props = withDefaults(defineProps<DropdownProps>(), {
    modelValue: null,
    disableTeleport: false,
    closeOnInteract: true,
    autoFocus: true,
    ignoredOutsideSelectors: () => [],
    triggerAsChild: false,
    triggerFullWidth: false,
    withPadding: true,
    fixedHeight: false,
    open: undefined,
    placement: DropdownPlacement.BOTTOM_START,
    sameWidth: false,
    selfScroll: false,
  });

  const emit = defineEmits<DropdownEmits>();

  // If we erroneously pass an item in the trigger slots and a custom trigger is passed, the item will be ignored
  const slots = useSlots();

  if (props.triggerIcon && slots.trigger) {
    throw new Error(
      'DEV ERROR: Dropdown can only have one trigger. It could be a triggerIcon or a slot named "trigger"',
    );
  }

  if (!slots.default) {
    throw new Error('Dropdown requires a default slot');
  }

  const internalValue = ref<string | null>(props.modelValue ?? null);

  watch(
    () => props.modelValue,
    (value) => {
      internalValue.value = value ?? null;
    },
  );

  const selectedValue = computed(() => internalValue.value);

  const itemLabels = reactive(new Map<string, string>());

  const displayValue = computed(() => {
    if (selectedValue.value === null) return '';
    return itemLabels.get(selectedValue.value) ?? selectedValue.value;
  });

  const selectItem = (value: string) => {
    internalValue.value = value;
    emit('update:modelValue', value);
  };

  const isListbox = computed(() => itemLabels.size > 0);

  const tabStopValue = computed(() => {
    if (selectedValue.value !== null && itemLabels.has(selectedValue.value)) {
      return selectedValue.value;
    }
    return itemLabels.keys().next().value ?? null;
  });

  const OPTION_SELECTOR = '[role="option"]';

  const onListboxKeydown = (event: KeyboardEvent) => {
    if (!isListbox.value) return;
    const container = event.currentTarget as HTMLElement;
    const options = Array.from(
      container.querySelectorAll<HTMLElement>(OPTION_SELECTOR),
    );
    if (!options.length) return;
    const current = (event.target as HTMLElement).closest<HTMLElement>(
      OPTION_SELECTOR,
    );
    const index = current ? options.indexOf(current) : -1;
    let next: number;
    switch (event.key) {
      case 'ArrowDown':
        next = Math.min(index + 1, options.length - 1);
        break;
      case 'ArrowUp':
        next = index < 0 ? 0 : Math.max(index - 1, 0);
        break;
      case 'Home':
        next = 0;
        break;
      case 'End':
        next = options.length - 1;
        break;
      default:
        return;
    }
    event.preventDefault();
    options[next]?.focus();
  };

  provide(DROPDOWN_CONTEXT_KEY, {
    selectedValue,
    isListbox,
    tabStopValue,
    selectItem,
    registerItem: (value: string, label: string) => {
      itemLabels.set(value, label);
      if (internalValue.value === null) selectItem(value);
    },
    unregisterItem: (value: string) => {
      itemLabels.delete(value);
    },
    close: () => onOpenChange({ open: false }),
  });

  const isOpen = ref(props.disabled ? false : (props.open ?? false));

  const shouldBlockOpening = computed(() => props.disabled === true);

  const onOpenChange = (details: { open: boolean }) => {
    if (details.open && shouldBlockOpening.value) return;
    isOpen.value = details.open;
    emit('update:open', details.open);
  };

  const onInteractOutside = (event: PopoverInteractOutsideEvent) => {
    if (!props.ignoredOutsideSelectors.length) return;
    const target = event.detail.originalEvent.target as HTMLElement | null;
    if (
      target &&
      props.ignoredOutsideSelectors.some((selector) => target.closest(selector))
    ) {
      event.preventDefault();
    }
  };

  watch(
    () => [props.open, props.disabled] as const,
    ([open, disabled]) => {
      if (disabled) {
        isOpen.value = false;
        return;
      }
      if (open !== undefined) isOpen.value = open;
    },
  );

  const triggerTestId = computed(
    () => `${props.testId || props.id || 'default'}-dropdown-trigger`,
  );
  const contentTestId = computed(
    () => `${props.testId || props.id || 'default'}-dropdown-content`,
  );

  /*
                      The reset class normalizes the real <button> Ark renders around the slot;
                      with `triggerAsChild` the classes merge onto the slot's own root instead,
                      where `border: none`/`background: none` would clobber its styling.
                    */
  const slotTriggerClasses = computed(() => [
    props.triggerAsChild ? undefined : dropdownTriggerResetClass,
    props.slotTriggerClass,
    { [slotTriggerFullWidthClass]: props.triggerFullWidth },
  ]);
</script>

<template>
  <ArkPopover.Root
    :open="isOpen"
    :close-on-interact-outside="closeOnInteract"
    :close-on-escape="true"
    :auto-focus="autoFocus"
    :initial-focus-el="initialFocusEl"
    :positioning="{ placement, sameWidth }"
    @open-change="onOpenChange"
    @interact-outside="onInteractOutside"
  >
    <ArkPopover.Context v-slot="popover">
      <ArkPopover.Trigger
        v-if="triggerIcon"
        as-child
        :class="dropdownIconTriggerClass"
        :data-test-id="triggerTestId"
        :aria-label="triggerAriaLabel"
      >
        <Button
          :variant="ButtonVariant.Icon"
          :icon-name="triggerIcon"
          :test-id="triggerTestId"
        />
      </ArkPopover.Trigger>
      <ArkPopover.Trigger
        v-if="!triggerIcon"
        :as-child="triggerAsChild"
        :class="slotTriggerClasses"
        :aria-label="triggerAriaLabel"
      >
        <slot
          name="trigger"
          :is-open="popover.open"
          :display-value="displayValue"
        >
          <Button
            :variant="ButtonVariant.Icon"
            :is-ghost="true"
            :icon-name="TUNDR_ICONS.OPTIONS"
          />
        </slot>
      </ArkPopover.Trigger>
    </ArkPopover.Context>
    <Teleport
      defer
      :disabled="disableTeleport"
      :to="`.${GLOBAL_OVERLAYS_TARGET}`"
    >
      <ArkPopover.Positioner :class="dropdownPositionerClass">
        <ArkPopover.Content
          :class="dropdownContentResetClass"
          v-bind="$attrs"
          @keydown="onListboxKeydown"
        >
          <ArkPopover.Context v-slot="popover">
            <div
              :data-test-id="contentTestId"
              :role="isListbox ? 'listbox' : undefined"
              :aria-label="isListbox ? triggerAriaLabel : undefined"
              :aria-labelledby="
                isListbox && !triggerAriaLabel
                  ? popover.getTriggerProps().id
                  : undefined
              "
              :class="[
                dropdownContentClass,
                { [dropdownContentPaddingClass]: withPadding },
                { [dropdownContentFixedHeightClass]: fixedHeight },
                { [dropdownContentSameWidthClass]: sameWidth },
                {
                  [dropdownContentSelfScrollClass]: selfScroll || fixedHeight,
                },
              ]"
            >
              <slot />
            </div>
          </ArkPopover.Context>
        </ArkPopover.Content>
      </ArkPopover.Positioner>
    </Teleport>
  </ArkPopover.Root>
</template>
